import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../services/api';
import toast from 'react-hot-toast';
import { useI18n } from '../contexts/I18nContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { t } = useI18n();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isInitialized, setIsInitialized] = useState(false);
  const initOnceRef = React.useRef(false);

  // 改进的认证检查逻辑，增加稳定性
  useEffect(() => {
    if (initOnceRef.current) {
      return;
    }
    initOnceRef.current = true;
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (storedToken) {
          // 设置axios默认header
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // 如果有存储的用户信息，先使用它
          if (storedUser) {
            try {
              const userData = JSON.parse(storedUser);
              setUser(userData);
              setToken(storedToken);
            } catch (e) {
              console.error('Failed to parse stored user data:', e);
            }
          }
          
          try {
            // 验证token有效性并获取最新用户信息
            const response = await axios.get('/users/me');
            const userData = response.data;
            setUser(userData);
            setToken(storedToken);
            // 更新存储的用户信息
            localStorage.setItem('user', JSON.stringify(userData));
          } catch (error) {
            console.error('Auth check failed:', error);
            // 清理无效的认证状态
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            setToken(null);
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Initialize auth failed:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []); // 空依赖数组，确保只在组件挂载时执行一次

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
      });

      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      toast.success(t('login.loginSuccessful'));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || t('login.loginFailedGeneric');
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post('/auth/register', {
        name,
        email,
        password,
      });

      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      toast.success(t('login.registrationSuccessful'));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || t('login.registrationFailedGeneric');
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    if (typeof window !== 'undefined') {
      window.didManualLogout = true;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    // 不在这里显示toast，由调用方决定是否显示
  };

  const loginWithCode = async (email, verificationCode, referralCode = null) => {
    try {
      const requestData = {
        email,
        verificationCode,
      };
      
      // 如果有推荐码，添加到请求中
      if (referralCode) {
        requestData.referralCode = referralCode;
      }

      const response = await axios.post('/auth/login/verify-code', requestData);

      const { token: newToken, user: userData, isReferralLogin, isNewUser } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      // 根据isReferralLogin显示不同的提示信息
      if (isReferralLogin && !isNewUser) {
        toast.success(t('login.accountExistsNoReferralPoints'), {
          duration: 4000,
        });
      } else {
        toast.success(t('login.accountExistsLoginSuccess'));
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loginWithGoogle = async (idToken, referralCode = null) => {
    try {
      const response = await axios.post('/auth/google/v2/verify', {
        idToken,
        referralCode,
      });

      const { token: newToken, user: userData, isReferralLogin, isNewUser } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      // 根据isReferralLogin显示不同的提示信息，isReferralLogin是新用户
      if (isReferralLogin && !isNewUser) {
        toast.success(t('login.accountExistsNoReferralPoints'), {
          duration: 4000,
        });
      } else {
        toast.success(t('login.googleLoginSuccessful'));
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || t('login.googleLoginFailedGeneric');
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    // 同时更新localStorage中的用户信息
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const value = {
    user,
    token,
    loading,
    isInitialized,
    login,
    loginWithCode,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    setToken,
    setUser,
    isAuthenticated: !!user && !!token && isInitialized,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
